import { useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";

import { showErrorToast } from "app/actions/toast_actions";
import { translate } from "app/filters/i18nFilter";
import { renderErrorToast } from "app/pages/sales-intelligence/common-components/ErrorToast/ErrorToast";
import { companyAnalysisQueryClient } from "app/pages/sales-intelligence/modules/company-analysis/queries/client";
import useInvalidateLeadsSearchQueries from "app/pages/sales-intelligence/pages/advanced-search/hooks/useInvalidateLeadsSearchQueries";
import useInvalidateOpportunityQueries from "app/pages/sales-intelligence/pages/opportunity-list/hooks/useInvalidateOpportunityQueries";
import type {
    IScoringConfigDto,
    ScoringConfigRule,
} from "app/pages/sales-intelligence/sub-modules/signals/types/scoring-config";
import { ScoringConfigType } from "app/pages/sales-intelligence/sub-modules/signals/types/scoring-config";
import { getFetchResponseErrorMessage } from "app/pages/sales-intelligence/sub-modules/signals/utils/getFetchResponseErrorMessage";

import { leadScoringQueryClient } from "../query/client";
import { leadScoringAgentQueryKeys } from "../query/key-factory/factory";
import useUpdateScoringConfigMutation, {
    DEFAULT_SCORING_CONFIG_ERROR_KEY,
} from "../query/mutations/useUpdateScoringConfigMutation";

type SaveRulesOptions = {
    onSuccess?: () => void;
    onError?: () => void;
    /**
     * i18n key for the leading part of the failure message (e.g. "Can not delete scoring rule.").
     * The server-provided reason is appended when available.
     */
    errorPrefixKey?: string;
    /**
     * When provided, the formatted failure message is passed here instead of showing a toast.
     * Use for modal flows where an inline error banner should be shown.
     */
    onErrorMessage?: (message: string) => void;
};

type UseSaveRulesOptions = Pick<SaveRulesOptions, "onSuccess" | "onError">;

type UseSaveRulesResult = {
    saveRules: (newRules: ScoringConfigRule, isAddSignal: boolean, saveOptions?: SaveRulesOptions) => Promise<void>;
    isPending: boolean;
};

function useSaveRules(configType: ScoringConfigType, options: UseSaveRulesOptions = {}): UseSaveRulesResult {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const invalidateLeadsSearchQueries = useInvalidateLeadsSearchQueries();
    const invalidateOpportunityQueries = useInvalidateOpportunityQueries();

    const { mutateAsync: updateScoringConfig, isPending } = useUpdateScoringConfigMutation(configType);

    const saveRules = (
        newRules: ScoringConfigRule,
        isAddSignal: boolean,
        saveOptions?: SaveRulesOptions,
    ): Promise<void> => {
        const queryKeys = leadScoringAgentQueryKeys.config(configType);
        const previousData = queryClient.getQueryData<IScoringConfigDto>(queryKeys);

        const updatedData = {
            rules: newRules,
            updatedAt: new Date().toISOString(),
            source: configType,
        };

        if (isAddSignal) {
            queryClient.setQueryData<IScoringConfigDto>(queryKeys, (prev) =>
                prev ? { ...prev, ...updatedData } : prev,
            );
        }

        return updateScoringConfig(
            { rules: newRules, errorPrefixKey: saveOptions?.errorPrefixKey },
            {
                onSuccess() {
                    if (configType === ScoringConfigType.Account) {
                        void leadScoringQueryClient.invalidateConfigQueries(ScoringConfigType.User);
                    }

                    if (!isAddSignal) {
                        queryClient.setQueryData<IScoringConfigDto>(queryKeys, (prev) =>
                            prev ? { ...prev, ...updatedData } : prev,
                        );
                    }

                    void leadScoringQueryClient.invalidateAllSummaries();
                    void invalidateLeadsSearchQueries();
                    void invalidateOpportunityQueries();
                    void companyAnalysisQueryClient.invalidateCompanyCardQueries();

                    options.onSuccess?.();
                    saveOptions?.onSuccess?.();
                },
                async onError(error) {
                    if (previousData && isAddSignal) {
                        queryClient.setQueryData(queryKeys, previousData);
                    }

                    const prefix = translate(saveOptions?.errorPrefixKey ?? DEFAULT_SCORING_CONFIG_ERROR_KEY);
                    const serverMessage = await getFetchResponseErrorMessage(error);
                    const content = serverMessage ? `${prefix} ${serverMessage}` : prefix;

                    if (saveOptions?.onErrorMessage) {
                        saveOptions.onErrorMessage(content);
                    } else {
                        dispatch(
                            showErrorToast(
                                renderErrorToast({
                                    content,
                                    minWidth: "auto",
                                }),
                            ),
                        );
                    }

                    options.onError?.();
                    saveOptions?.onError?.();
                },
            },
        );
    };

    return { saveRules, isPending };
}

export default useSaveRules;
